import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { DsAvatarComponent } from './ds-avatar.component';
import {
  DsHeaderButtonComponent,
  type DsHeaderButtonNotification,
  type DsHeaderButtonState,
} from './ds-header-button.component';
import { DsLogoComponent } from './ds-logo.component';
import type { DsIconName } from '../icons/ds-icons';

interface DsAppHeaderItemBase {
  /** Stable application-defined identifier used for tracking and click events. */
  id: string;
  label: string;
  ariaLabel?: string;
  notification?: DsHeaderButtonNotification;
  notificationCount?: number;
  notificationAriaLabel?: string;
  disabled?: boolean;
}

export interface DsAppHeaderIconItem extends DsAppHeaderItemBase {
  visual?: 'icon';
  icon: DsIconName | string;
  initials?: never;
}

export interface DsAppHeaderAvatarItem extends DsAppHeaderItemBase {
  visual: 'avatar';
  initials: string;
  icon?: never;
}

export type DsAppHeaderItem = DsAppHeaderIconItem | DsAppHeaderAvatarItem;

export interface DsAppHeaderItemSelection {
  item: DsAppHeaderItem;
  id: string;
  event: MouseEvent;
}

@Component({
  selector: 'ds-app-header',
  standalone: true,
  imports: [DsAvatarComponent, DsHeaderButtonComponent, DsLogoComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <header class="ds-app-header">
      <ds-logo
        class="ds-app-header__logo"
        size="m"
        [invert]="true"
        [href]="logoHref"
        [target]="logoTarget"
        [rel]="logoRel"
        [ariaLabel]="logoAriaLabel"
        [actionable]="true"
        (clicked)="logoClicked.emit($event)"
      />

      <nav class="ds-app-header__actions" [attr.aria-label]="actionsAriaLabel">
        @for (item of items; track item.id) {
          <ds-header-button
            class="ds-app-header__item"
            [attr.data-item-id]="item.id"
            [label]="item.label"
            [ariaLabel]="item.ariaLabel || item.label"
            [icon]="item.visual === 'avatar' ? '' : item.icon"
            [notification]="item.notification || 'none'"
            [notificationCount]="item.notificationCount ?? 0"
            [notificationAriaLabel]="item.notificationAriaLabel || ''"
            size="s"
            [iconOnly]="true"
            [disabled]="item.disabled || false"
            [state]="itemState(item.id)"
            (clicked)="onItemClicked(item, $event)"
          >
            @if (item.visual === 'avatar') {
              <ds-avatar
                dsItemButtonVisual
                [initials]="item.initials"
                size="m"
                variant="app"
                [decorative]="true"
              />
            }
          </ds-header-button>
        }
      </nav>
    </header>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }

    .ds-app-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      box-sizing: border-box;
      width: 100%;
      min-width: 0;
      height: var(--ds-app-header-height);
      padding: 0 var(--ds-app-header-padding-x);
      background: var(--ds-app-header-background);
    }

    .ds-app-header__logo {
      display: inline-flex;
      flex: 0 0 auto;
    }

    .ds-app-header__actions {
      display: flex;
      align-items: center;
      gap: var(--ds-app-header-actions-gap);
      flex: 0 0 auto;
      margin-left: auto;
    }

    .ds-app-header__item {
      --ds-header-button-background: var(--ds-app-header-action-background);
      --ds-header-button-hover-background: var(--ds-app-header-action-hover-background);
      --ds-header-button-foreground: var(--ds-app-header-action-foreground);
    }
  `],
})
export class DsAppHeaderComponent {
  @Input() items: readonly DsAppHeaderItem[] = [];
  @Input() selectedItemId: string | null = null;
  @Input() actionsAriaLabel = 'Acciones de la aplicación';
  @Input() logoAriaLabel = 'AFP Capital';
  @Input() logoHref = '';
  @Input() logoTarget = '';
  @Input() logoRel = '';

  /** Lets the consuming application decide the logo action (usually navigation to home). */
  @Output() logoClicked = new EventEmitter<MouseEvent>();
  @Output() itemSelected = new EventEmitter<DsAppHeaderItemSelection>();

  itemState(id: string): DsHeaderButtonState {
    return this.selectedItemId === id ? 'selected' : 'default';
  }

  onItemClicked(item: DsAppHeaderItem, event: MouseEvent): void {
    this.itemSelected.emit({ item, id: item.id, event });
  }
}
